import React from 'react';
import { formatContractDateTime } from '../../../utils/contractDateUtils';
import { finesService, fmtAED, errorMessage } from '../../../services/finesService';
import { toast } from '../../../ui/feedback';
import { Pill } from './ui';

// RTA payments (owner, 15 Oct 2026; engine/docs/RTA_PAYMENTS_SPEC.md): the words the Fines page uses for a receipt -
// the Payments dialog, the Status pill and the fine window say the same thing the same way.

/** "dd/mm/yyyy HH:MM" of a receipt's time (Dubai wall time, sent without a zone); unreadable text as written. */
export const paidOn = (v) => formatContractDateTime(v) || (v ? String(v) : '');

/** "dd/mm" for a pill. */
export const paidDay = (v) => {
    const s = formatContractDateTime(v);
    return s ? s.slice(0, 5) : '';
};

/** How it was paid: the method and where, as the receipt prints them. */
export const howPaid = (p) => [p?.method, p?.paid_where].filter(Boolean).join(' · ');

/** Who paid. RTA prints the company (the traffic-file owner) on every receipt, whoever paid: a payment is the company's
 *  unless its receipt was uploaded as the customer's (owner, 16 Oct 2026 - the engine's `paid_by` / `company`); an
 *  uploaded receipt with no payer is the owner paying on RTA's public fines page, which prints no name. */
export const payerText = (p) => {
    if (!p) return '';
    if (p.paid_by === 'customer') return 'Customer (receipt sent by the customer)';
    // a receipt read by AI (owner, 21 Oct 2026): where it was paid is in "How" ("Sharjah Police - read by AI")
    if (p.company && /read by AI$/.test(p.paid_where || '')) return 'Company';
    // an Abu Dhabi TAMM receipt prints no name either (owner, 19 Oct 2026: the TAMM reader)
    if (p.company && /TAMM/.test(p.paid_where || '')) return 'Company (Abu Dhabi TAMM)';
    if (p.company && !p.payer && p.source === 'upload') return 'Company (RTA public page)';
    if (p.company) return p.payer ? `Company · ${p.payer}` : 'Company';
    return p.payer || 'Not named on the receipt';
};

/** A receipt's hover text: method, where, its reference, when and how much. */
export const receiptTitle = (p) => [
    howPaid(p),
    p?.ref ? `receipt ${p.ref}` : '',
    paidOn(p?.paid_at),
    p?.amount !== undefined && p?.amount !== null ? fmtAED(p.amount) : '',
].filter(Boolean).join(' · ');

/** One entry a receipt: the same receipt is never shown twice for one fine. */
export const byReceipt = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
        const k = `${p?.ref || ''}|${p?.paid_at || ''}|${p?.amount ?? ''}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
    });
};

/** "checked up to dd/mm/yyyy": how far the engine's read of the company RTA account has come; '' when not sent. */
export const checkedUpTo = (v) => {
    const d = paidOn(v);
    return d ? `checked up to ${d}` : '';
};

// "Paid outside company receipts" (owner, 15 Oct 2026): said ONLY when the engine's lookup says its read of the company
// RTA account has reached that fine's payment (`covered` true) and still no receipt holds it. Covered false or not sent
// = the weekly read has not come to that payment yet: a fine paid yesterday is not "outside" anything, so nothing is
// shown. One rule for the Status pill and the fine window.
export const OUTSIDE_LABEL = 'Paid outside company receipts';
export const paidOutside = (fine, pay) => !!pay && pay.covered === true && fine?.status === 'Paid' && !byReceipt(pay.paid).length;
// The manual "Company / Customer" choice (fines_history.paid_by, what billing reads) is hidden once a company receipt
// says the company paid RTA (owner, 15 Oct 2026: "ليه لسه فوق موجود عميل وشركة"): the receipt answers it. It stays for a
// fine with no company receipt (corporate, paid outside, another payer), and when it was set to Customer - a
// disagreement with the receipt someone has to see. Nothing is written: billing reads paid_by as before.
export const companyReceipt = (pay) => byReceipt(pay?.paid).some((p) => !!p?.company);
export const showPaidBy = (fine, pay) => !companyReceipt(pay) || fine?.paid_by === 'customer';
export const outsideTitle = (pay) => [
    'RTA lists it as paid, but no receipt holds it: the company RTA account\'s history is read past its payment and no PDF was uploaded under Payments',
    checkedUpTo(pay?.covered_to),
].filter(Boolean).join(' · ');

// An impound fee paid for the car at a minute it has several engine fines (lookup match 'impound_review'): it is one of
// them, not each - shown as a question on every one, with the others named (RTA_PAYMENTS_SPEC.md).
export const impoundReview = (p) => p?.match === 'impound_review';
export const reviewNote = (p) => {
    const others = (p?.engine_tickets || []).filter(Boolean);
    return `Impound paid for ONE of ${others.length || 'several'} fines of this car at that minute${others.length ? ` (${others.join(', ')})` : ''} - check which`;
};

// A receipt's PDF in a new tab - the Proof column and the fine window. The tab opens on the click itself (one opened after
// the wait is blocked as a pop-up) and the PDF lands in it; a blocked tab saves the file instead.
export const openReceipt = async (ref) => {
    const tab = window.open('', '_blank');
    try {
        const url = await finesService.rtaReceiptPdf(ref);
        if (tab) tab.location.href = url;
        else {
            const a = document.createElement('a');
            a.href = url; a.download = `${ref}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 300000);
    } catch (e) {
        if (tab) tab.close();
        toast.error(e?.response?.status === 404 ? `No PDF is kept for receipt ${ref}` : errorMessage(e));
    }
};

// The company paid RTA (its receipt) but the fine is marked paid by the customer - set by hand, or by the nightly Ejar
// check when its line is inactive on Ejar - so the customer is not charged (owner, 16 Oct 2026: 7042501528, paid by the
// company 30/09, its Ejar line made inactive 01/10). One rule for the Proof column, the fine window and the engine's
// Paid by filter "conflict" (api/data.py).
export const NOT_CHARGED_LABEL = 'Company paid · customer not charged';
export const customerNotCharged = (fine, pay) => companyReceipt(pay) && fine?.paid_by === 'customer';
export const notChargedTitle = (fine, pay) => {
    const p = byReceipt(pay?.paid).find((x) => !!x?.company);
    return [`Paid at RTA${p && paidOn(p.paid_at) ? ` on ${paidOn(p.paid_at)}` : ''} - the company's payment, as no customer receipt was uploaded for it`,
        'but it is marked paid by the customer, so the customer is not charged',
        fine?.ejar_note || '',
        "if the customer paid: upload the receipt he sent under Payments as Customer; if not: the fine goes back on the Ejar contract"].filter(Boolean).join(' · ');
};

const day = (p) => (paidDay(p.paid_at) ? ` · ${paidDay(p.paid_at)}` : '');

/** One receipt as a pill that opens it: the proof window on the page holding `target` (onOpen), else the PDF. */
const ReceiptButton = ({ p, target, onOpen, tone, size, title, children }) => (
    <button type="button" className="cursor-pointer" title={`${title} · click to see the receipt`}
            onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen({ ref: p.ref, ...target, p }); else openReceipt(p.ref); }}>
        <Pill tone={tone} size={size}>{children}</Pill>
    </button>
);

// The Proof column (owner, 16 Oct 2026: "عايز صور اثباتات فى عمود لوحده اسمه ابروف علشان اعرف مين اللى دفع فعلا
// بالاثبات"): who paid the fine, when, as RTA's own receipt says - a click opens that receipt; facts only, never a "paid
// by" set. `pay` = the fine's entry of the page's one lookup; undefined while (or when) the lookup is not read - then
// nothing. `onOpen({ref, ticket, at, p})` shows the proof window (ReceiptPageModal) on the page that holds this fine - an
// impound by its offence minute. Kept here (not in FinesTab) so it is tested on its own.
export const ProofCell = ({ fine, pay, size = 'sm', onOpen }) => {
    if (!pay) return null;
    const paid = byReceipt(pay.paid);
    const impounds = byReceipt(pay.impounds);
    const outside = paidOutside(fine, pay);
    if (!paid.length && !impounds.length && !outside) return <span className="ds-cell-empty">—</span>;
    return (
        <div className="flex flex-wrap items-center justify-center gap-1">
            {customerNotCharged(fine, pay) ? <Pill tone="red" size={size} title={notChargedTitle(fine, pay)}>{NOT_CHARGED_LABEL}</Pill> : null}
            {paid.map((p, i) => (
                <ReceiptButton key={`paid-${p.ref}-${i}`} p={p} onOpen={onOpen} target={{ ticket: String(fine?.ticket_number ?? ''), at: '' }}
                               tone={p.company ? 'indigo' : 'purple'} size={size}
                               title={`${payerText(p)} · ${receiptTitle(p)}`}>
                    {p.paid_by === 'customer' ? 'Paid by customer' : p.company ? 'Paid by company' : `Paid by ${p.payer || 'other'}`}{day(p)}
                </ReceiptButton>
            ))}
            {outside ? <Pill tone="gray" size={size} title={outsideTitle(pay)}>{OUTSIDE_LABEL}</Pill> : null}
            {/* a paid impound is its own payment, never shown as the fine's (RTA_PAYMENTS_SPEC.md); one the car's
                several fines of that minute share is a question, not this fine's impound (16 Oct 2026 review) */}
            {impounds.map((p, i) => (
                <ReceiptButton key={`imp-${p.ref}-${i}`} p={p} onOpen={onOpen} target={{ ticket: '', at: p.offence_at || '' }}
                               tone={impoundReview(p) ? 'amber' : 'gray'} size={size}
                               title={`${impoundReview(p) ? `${reviewNote(p)} · ` : ''}${p.offence ? `${p.offence} · ` : ''}${payerText(p)} · ${receiptTitle(p)}`}>
                    {impoundReview(p) ? 'Impound: which fine?' : 'Impound paid'}{day(p)}
                </ReceiptButton>
            ))}
        </div>
    );
};
